import _extends from 'babel-runtime/helpers/extends';
import _defineProperty from 'babel-runtime/helpers/defineProperty';
import _objectWithoutProperties from 'babel-runtime/helpers/objectWithoutProperties';
import _Object$getPrototypeOf from 'babel-runtime/core-js/object/get-prototype-of';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import _Array$from from 'babel-runtime/core-js/array/from';
import _pick from 'lodash/pick';
import _isNil from 'lodash/isNil'; /* eslint-disable no-underscore-dangle, jsx-a11y/no-autofocus */

import React, { PureComponent } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import Tick from '@wh/svg-icons/Tick';
import X from '@wh/svg-icons/X';
import ThemeConsumer from '@sitebase/theme-context/es/ThemeConsumer';

import { getAllowedAttributes } from '../props-filters';
import propTypeClassName from '../propTypeClassName';
import { makeOutfitParser, category } from '../outfit-parser';
import outfitPropValidator from '../outfitPropValidator';
import InputIcon from './InputIcon';
import * as style from './Input.style';

var sizesList = ['tiny', 'small', 'normal', 'large', 'x-large'];
var statusesList = ['initial', 'error', 'success'];
var modifiersList = ['stretched', 'bare'];

var inputSpecificProps = ['ariaLabel', 'autoFocus', 'autoComplete', 'form', 'inputMode', 'list', 'min', 'max', 'maxLength', 'minLength', 'multiple', 'name', 'pattern', 'readOnly', 'spellCheck', 'step', 'autoCapitalize', 'autoCorrect'];

var getInputProps = function getInputProps(props) {
  return _pick(props, inputSpecificProps);
};

var parseOutfit = makeOutfitParser(category('size', sizesList), category('status', statusesList), category('modifiers', modifiersList, true));

var getOutfitProps = function getOutfitProps(outfit) {
  var parsedOutfit = parseOutfit(outfit);
  var size = _Array$from(parsedOutfit.get('size')).pop() || 'normal';
  var status = _Array$from(parsedOutfit.get('status')).pop() || 'initial';
  var bare = parsedOutfit.get('modifiers').has('bare');

  return {
    isStretched: parsedOutfit.get('modifiers').has('stretched'),
    size: size,
    status: status,
    bare: bare,
    hasError: status === 'error',
    isSuccessful: status === 'success'
  };
};

var renderIcons = function renderIcons(icons) {
  return icons.map(function (icon, index) {
    return React.createElement(
      InputIcon,
      { 'data-test-id': 'Input.icon--' + index, key: icon.key || index },
      icon
    );
  });
};

export var inputOutfitPropValidator = outfitPropValidator([].concat(sizesList, statusesList, modifiersList));

export var defaultProps = {
  type: 'text',
  outfit: 'initial normal'
};

var Input = function (_PureComponent) {
  _inherits(Input, _PureComponent);

  function Input() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck(this, Input);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn(this, (_ref = Input.__proto__ || _Object$getPrototypeOf(Input)).call.apply(_ref, [this].concat(args))), _this), _this.state = {
      focused: false
    }, _this.initialRender = true, _this._storeInput = function (input) {
      _this._input = input;
    }, _this._onClick = function (event) {
      if (_this._input) {
        _this._input.focus();
      }
      if (_this.props.onClick) {
        _this.props.onClick(event);
      }
    }, _this._onFocus = function (event) {
      if (_this.props.onFocus) {
        event.persist();
        _this.setState({ focused: true }, function () {
          return _this.props.onFocus(event);
        });
      } else {
        _this.setState({ focused: true });
      }
    }, _this._onBlur = function (event) {
      if (_this.props.onBlur) {
        event.persist();
        _this.setState({ focused: false }, function () {
          return _this.props.onBlur(event);
        });
      } else {
        _this.setState({ focused: false });
      }
    }, _this._isShrunk = function () {
      var _this$props = _this.props,
          value = _this$props.value,
          defaultValue = _this$props.defaultValue,
          placeholder = _this$props.placeholder,
          floatingLabel = _this$props.floatingLabel;
      var focused = _this.state.focused;

      var defaultValueDuringInitialRender = _this.initialRender && defaultValue;
      var nativeInputValue = _this._input && _this._input.value;
      return floatingLabel && (value || defaultValueDuringInitialRender || focused || placeholder || nativeInputValue);
    }, _this._renderFloatingLabel = function (floatingLabel, size) {
      return React.createElement(
        'div',
        { 'data-test-id': 'Input.floating-label', className: style.label(_this._isShrunk(), size) },
        React.createElement(
          'div',
          null,
          floatingLabel
        )
      );
    }, _this._renderWithTheme = function (theme) {
      var _cx;

      var _this$props2 = _this.props,
          className = _this$props2.className,
          disabled = _this$props2.disabled,
          _this$props2$icons = _this$props2.icons,
          icons = _this$props2$icons === undefined ? [] : _this$props2$icons,
          onChange = _this$props2.onChange,
          onKeyDown = _this$props2.onKeyDown,
          onKeyPress = _this$props2.onKeyPress,
          onKeyUp = _this$props2.onKeyUp,
          outfit = _this$props2.outfit,
          placeholder = _this$props2.placeholder,
          floatingLabel = _this$props2.floatingLabel,
          _this$props2$type = _this$props2.type,
          type = _this$props2$type === undefined ? 'text' : _this$props2$type,
          value = _this$props2.value,
          defaultValue = _this$props2.defaultValue,
          onTouchStart = _this$props2.onTouchStart,
          onTouchCancel = _this$props2.onTouchCancel,
          onTouchMove = _this$props2.onTouchMove,
          onTouchEnd = _this$props2.onTouchEnd,
          ariaLabel = _this$props2.ariaLabel,
          restProps = _objectWithoutProperties(_this$props2, ['className', 'disabled', 'icons', 'onChange', 'onKeyDown', 'onKeyPress', 'onKeyUp', 'outfit', 'placeholder', 'floatingLabel', 'type', 'value', 'defaultValue', 'onTouchStart', 'onTouchCancel', 'onTouchMove', 'onTouchEnd', 'ariaLabel']);

      var focused = _this.state.focused;


      var inputProps = getInputProps(restProps);
      var allowedAttributes = getAllowedAttributes(restProps);

      var _getOutfitProps = getOutfitProps(outfit),
          hasError = _getOutfitProps.hasError,
          bare = _getOutfitProps.bare,
          isStretched = _getOutfitProps.isStretched,
          isSuccessful = _getOutfitProps.isSuccessful,
          size = _getOutfitProps.size,
          status = _getOutfitProps.status;

      var generatedClassName = cx(style.base, (_cx = {}, _defineProperty(_cx, style.hidden, /hidden/i.test(type)), _defineProperty(_cx, style.disabled, disabled), _defineProperty(_cx, style[theme], true), _defineProperty(_cx, style.sizes[size], true), _defineProperty(_cx, style.sizes.stretched, isStretched), _defineProperty(_cx, style.statuses[status], !focused), _defineProperty(_cx, style.focused[theme], focused), _cx));

      return (
        /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */
        React.createElement(
          'div',
          _extends({}, allowedAttributes, {
            onClick: _this._onClick,
            onTouchStart: onTouchStart,
            onTouchCancel: onTouchCancel,
            onTouchMove: onTouchMove,
            onTouchEnd: onTouchEnd,
            className: cx(generatedClassName, className)
          }),
          React.createElement(
            'div',
            { className: style.container },
            floatingLabel && _this._renderFloatingLabel(floatingLabel, size),
            React.createElement(
              'div',
              { className: style.inputContainer(_this._isShrunk(), size) },
              React.createElement('input', _extends({}, inputProps, {
                'aria-label': ariaLabel,
                ref: _this._storeInput,
                'data-test-id': 'Input.input',
                disabled: disabled,
                defaultValue: defaultValue,
                onBlur: _this._onBlur,
                onChange: onChange,
                onFocus: _this._onFocus,
                onKeyDown: onKeyDown,
                onKeyPress: onKeyPress,
                onKeyUp: onKeyUp,
                placeholder: placeholder || '',
                type: type,
                value: value
              }))
            )
          ),
          renderIcons(icons),
          React.createElement(
            InputIcon,
            {
              'data-test-id': 'Input.icon--error',
              className: style.errorIcon,
              shown: !focused && !bare && hasError
            },
            React.createElement(X, null)
          ),
          React.createElement(
            InputIcon,
            {
              'data-test-id': 'Input.icon--success',
              className: style.successIcon,
              shown: !focused && !bare && isSuccessful
            },
            React.createElement(Tick, null)
          )
        )
      );
    }, _temp), _possibleConstructorReturn(_this, _ret);
  }

  _createClass(Input, [{
    key: 'componentDidMount',
    value: function componentDidMount() {
      this.initialRender = false;
    }

    /**
     * Getter/Setter of Input value
     * @public
     * @param {String} value
     * @returns {String}
     */

  }, {
    key: 'render',

    /* eslint-enable complexity */

    value: function render() {
      return React.createElement(
        ThemeConsumer,
        null,
        this._renderWithTheme
      );
    }
  }, {
    key: 'value',
    get: function get() {
      return this._input ? this._input.value : undefined;
    },
    set: function set(value) {
      if (this._input && _isNil(this.props.value)) {
        this._input.value = value;
      }
    }

    // eslint-disable-next-line complexity


    /* eslint-disable complexity */

  }]);

  return Input;
}(PureComponent);

Input.propTypes = {
  /** Callback functions that is called when an Input clicked */
  onClick: PropTypes.func,
  /** Callback functions that is called when an input lost focus */
  onBlur: PropTypes.func,
  /** Callback functions that is called when an input got focus */
  onFocus: PropTypes.func,
  /** Callback functions that is called when a key was hit in the input */
  onKeyDown: PropTypes.func,
  /** Callback functions that is called when a key was pressed in the input */
  onKeyPress: PropTypes.func,
  /** Callback functions that is called when a key was released in the input */
  onKeyUp: PropTypes.func,
  /** Callback function that is fired when the input value changed */
  onChange: PropTypes.func,
  /** Callback function that is fired when one or more touch points have been disrupted in an implementation-specific manner (for example, too many touch points are created) */
  onTouchCancel: PropTypes.func,
  /** Callback function that  fires when one or more touch points are removed from the touch  */
  onTouchEnd: PropTypes.func,
  /** Callback function that is fired when one or more touch points are moved along the touch surface. */
  onTouchMove: PropTypes.func,
  /** Callback function that is fired when one or more touch points are placed on the touch surface */
  onTouchStart: PropTypes.func,
  /** Aria label for input field only */
  ariaLabel: PropTypes.string,
  /**
   * Type: **string**.
   *
   * @see See [Commonly used attributes](#components-common-props) for a detailed description.
   *
   * Options:
   *
   *    - Sizes:
   *      - "**tiny**"
   *      - "**small**"
   *      - "**normal**"
   *      - "**large**"
   *      - "**x-large**"
   *    - Statuses: (adds appropriate status icons and coloring)
   *      - "**error**"
   *      - "**success**"
   *      - "**initial**".
   *    - Modifiers:
   *      - "**stretched**" (have height 100%)
   *      - "**bare**" (do not show status icons)
   */
  outfit: inputOutfitPropValidator,
  /**
   * Type: **string**.
   *
   * @see See [Commonly used attributes](#components-common-props) for a detailed description.
   */
  className: propTypeClassName,
  /** Defines whether input is disabled or not */
  disabled: PropTypes.bool,
  /** Placeholder value to show until value does not exist */
  placeholder: PropTypes.string,
  /** Text of floating label  */
  floatingLabel: PropTypes.string,
  /** Specifies the type of input to display. Default type is text. */
  type: PropTypes.oneOf(['password', 'text', 'email', 'number', 'tel', 'search', 'url', 'hidden', 'date', 'datetime-local', 'month', 'time', 'week']),
  /** Array of nodes to be added as icons to the input. */
  icons: PropTypes.arrayOf(PropTypes.node),
  /** Value of the input */
  value: PropTypes.string,
  /** Default value of the input */
  defaultValue: PropTypes.string
};
Input.defaultProps = defaultProps;
export default Input;